import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "motion/react";
import { AmbientOrb, Button, Text } from "@andrum/ui";
import { isAuthEnforced } from "@/lib/supabase/client";
import { durations, easings, springs } from "@/lib/motion/tokens";

export function WelcomeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authOn = isAuthEnforced();
  const reduce = useReducedMotion();

  return (
    <div className="flex h-dvh max-h-dvh flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain bg-background [-webkit-overflow-scrolling:touch]">
      <div className="relative min-h-[34dvh] shrink-0 overflow-hidden">
        <motion.div
          className="absolute inset-0"
          {...(!reduce
            ? {
                animate: {
                  background: [
                    "radial-gradient(circle at 30% 20%, hsl(var(--primary-soft)) 0%, transparent 50%)",
                    "radial-gradient(circle at 70% 40%, hsl(var(--accent-soft)) 0%, transparent 55%)",
                    "radial-gradient(circle at 30% 20%, hsl(var(--primary-soft)) 0%, transparent 50%)",
                  ],
                },
                transition: {
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut" as const,
                },
              }
            : {})}
        />
        <AmbientOrb className="-top-16 -right-16 h-48 w-48 opacity-50" />
        <AmbientOrb
          colorClassName="bg-accent"
          className="bottom-6 -left-12 h-40 w-40 opacity-40"
        />
        <div className="absolute inset-0 flex items-center justify-center pt-[env(safe-area-inset-top)]">
          <motion.div
            className="flex items-center justify-center"
            {...(!reduce
              ? {
                  animate: { scale: [1, 1.03, 1] },
                  transition: {
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut" as const,
                  },
                }
              : {})}
          >
            <img
              src="/app-icon-inner.svg"
              alt=""
              width={88}
              height={88}
              className="h-[88px] w-[88px] rounded-[22px] shadow-lg"
              aria-hidden
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        className="flex flex-col justify-end gap-3 px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-2"
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: durations.normal, ease: easings.enter }}
      >
        <Text variant="display" as="h1" className="text-2xl leading-tight">
          {t("auth.welcomeTitle")}
        </Text>
        <Text variant="muted" className="line-clamp-3 text-sm leading-snug">
          {t("auth.welcomeLead")}
        </Text>

        <div className="space-y-2.5 pt-1">
          <motion.div
            {...(!reduce
              ? {
                  whileTap: { scale: 0.98 },
                  transition: springs.responsive,
                }
              : {})}
          >
            <Button
              fullWidth
              className="min-h-11"
              onClick={() => {
                void navigate({ to: authOn ? "/signup" : "/onboarding" });
              }}
            >
              {t("auth.getStarted")}
            </Button>
          </motion.div>
          <Button
            fullWidth
            variant="ghost"
            className="min-h-11"
            onClick={() => {
              void navigate({ to: authOn ? "/login" : "/app/today" });
            }}
          >
            {t("auth.haveAccount")}
          </Button>
          {!authOn ? (
            <Link
              to="/app/today"
              className="flex w-full min-h-10 items-center justify-center text-sm text-foreground-subtle"
            >
              {t("auth.continueAsGuest")}
            </Link>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
}
